import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
  castArray,
  filter,
  find,
  includes,
  map,
  pickBy,
  toLower,
} from 'lodash';
import { Observable, of, throwError } from 'rxjs';

import {
  ApplicationConfig,
  ApplicationConfigRaw,
  ApplicationPartialConfig,
  ClientTypeType,
  Partners,
  Proposal,
  Quote,
} from '@lockton-apps/shared/interfaces';
import { CONFIGS, PARTIALS } from '@lockton-apps/shared/static/proposal-config';
import { defaultLocale } from '@lockton-apps/shared/util/localization';

import {
  ProposalBuilderService,
  ProposalMocksService,
} from '@lockton-apps/shared/util/proposal-utils';
import { getDevConfig } from '@lockton-apps/shared/util/helpers';

@Injectable({ providedIn: 'root' })
export class ProposalsService {
  private cachedConfig!: ApplicationConfig;

  constructor(
    private http: HttpClient,
    @Inject('BASE_API_URL') private baseUrl: string,
    private proposalMocksService: ProposalMocksService,
    private readonly proposalBuilder: ProposalBuilderService
  ) {}

  private get isUsingMocks(): boolean {
    return this.proposalMocksService.isUsingMocks;
  }

  getConfig(
    portalId: number,
    configId: number,
    locale: string,
    clientType: ClientTypeType | undefined
  ): Observable<ApplicationConfig> {
    const config: ApplicationConfigRaw = this.findConfig(
      portalId,
      configId,
      toLower(locale)
    );
    const partials: Record<string, ApplicationPartialConfig> =
      this.findConfigPartials(portalId, config.locale);

    if (config) {
      this.cachedConfig = this.proposalBuilder.buildConfig(
        config,
        partials,
        clientType
      );
    }
    if (getDevConfig('proposalForm.runLocally')) {
      this.proposalMocksService.setConfig(this.cachedConfig);
    }

    return this.cachedConfig
      ? of(this.cachedConfig)
      : throwError(
          () =>
            `No config found for provided parameters:\nPortal: ${portalId}\nConfig: ${configId}\nLocale: ${config.locale}`
        );
  }

  getPartnerConfigs(
    portalId: Partners,
    locale: string,
    clientType: ClientTypeType | undefined
  ): Observable<ApplicationConfig[]> {
    const partials: Record<string, ApplicationPartialConfig> =
      this.findConfigPartials(portalId, locale);

    return of(
      map(filter(CONFIGS[portalId], { locale }), (config) =>
        this.proposalBuilder.buildConfig(config, partials, clientType)
      )
    );
  }

  getProposalById(
    proposalId: string,
    portalId: number,
    configId: number,
    clientId: number
  ): Observable<Proposal> {
    if (this.isUsingMocks) {
      return of(
        this.proposalMocksService.getLocalProposal(
          portalId,
          configId,
          clientId,
          proposalId
        )
      );
    }

    return this.http.get<Proposal>(`${this.baseUrl}/proposal/${proposalId}`);
  }

  getProposalByQuoteId(quoteId: number): Observable<Proposal> {
    return this.http.get<Proposal>(`${this.baseUrl}/quote/${quoteId}/proposal`);
  }

  saveProposal(proposal: Proposal, configId?: number): Observable<Proposal> {
    if (this.isUsingMocks && configId) {
      this.cachedConfig.version;
      return this.proposalMocksService.saveProposalLocally(proposal);
    }

    return this.http.put<Proposal>(`${this.baseUrl}/proposal`, proposal);
  }

  submitProposal(proposal: Proposal, configId?: number): Observable<Quote[]> {
    if (this.isUsingMocks && configId) {
      return this.proposalMocksService.submitProposalLocally(proposal);
    }

    return this.http.put<Quote[]>(
      `${this.baseUrl}/proposal/submission`,
      proposal
    );
  }

  private findConfig(
    portalId: Partners,
    configId: number,
    locale: string
  ): ApplicationConfigRaw {
    const portalConfigs: ApplicationConfigRaw[] = CONFIGS[portalId];
    let config: ApplicationConfigRaw | undefined = find(portalConfigs, {
      id: `${portalId}-${configId}`,
      portalId,
      locale,
    });

    if (!config) {
      console.warn(
        `No config for locale ${locale} found. Loading ${defaultLocale.id} instead.`
      );

      config = find(portalConfigs, {
        id: `${portalId}-${configId}`,
        portalId,
        locale: defaultLocale.id,
      });
    }

    return config as ApplicationConfigRaw;
  }

  private findConfigPartials(
    _portalId: Partners,
    _locale: string
  ): Record<string, ApplicationPartialConfig> {
    return pickBy(
      PARTIALS,
      ({ portalId, locale }) =>
        (includes(castArray(portalId), _portalId) || portalId === '*') &&
        (includes(castArray(locale), toLower(_locale)) || locale === '*')
    );
  }
}
